import { useState, useEffect, useRef, Portal } from './wp';
import { SearchIcon, BagIcon, MenuIcon, ChevronIcon } from './icons';
import SearchPanel from './SearchPanel';
import CartDrawer from './CartDrawer';
import MobileNav from './MobileNav';
import { fetchCart } from './cart';

function Announcement({ announce }) {
    if (!announce || !announce.enabled) {
        return null;
    }

    const content = (text) => (announce.link ? <a href={announce.link}>{text}</a> : text);

    return (
        <div className="tsa-hdr__announce">
            {announce.mobile && <div className="tsa-hdr__announce-msg tsa-hdr__announce-msg--mobile">{content(announce.mobile)}</div>}
            {announce.desktop && <div className="tsa-hdr__announce-msg tsa-hdr__announce-msg--desktop">{content(announce.desktop)}</div>}
        </div>
    );
}

function NavItem({ item, open, onOpen, onClose }) {
    const timer = useRef(null);
    const setOpen = (value) => (value ? onOpen() : onClose());

    if (!item.children || !item.children.length) {
        return (
            <li className="tsa-hdr__navitem">
                <a href={item.url}>{item.label}</a>
            </li>
        );
    }

    // A short close delay keeps the submenu reachable across the gap between the
    // trigger and the panel.
    const cancel = () => {
        if (timer.current) {
            window.clearTimeout(timer.current);
            timer.current = null;
        }
    };

    return (
        <li
            className={'tsa-hdr__navitem tsa-hdr__navitem--has-children' + (open ? ' is-open' : '')}
            onMouseEnter={() => {
                cancel();
                setOpen(true);
            }}
            onMouseLeave={() => {
                cancel();
                timer.current = window.setTimeout(() => setOpen(false), 150);
            }}
            onFocus={() => setOpen(true)}
            onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) {
                    setOpen(false);
                }
            }}
        >
            <button
                type="button"
                aria-expanded={open}
                onClick={() => setOpen(!open)}
                onKeyDown={(event) => {
                    if (event.key === 'Escape') {
                        setOpen(false);
                    }
                }}
            >
                {item.label}
                <ChevronIcon />
            </button>
            {/* Anything longer than five links reads better in two columns. */}
            <ul className={'tsa-hdr__submenu' + (item.children.length > 5 ? ' tsa-hdr__submenu--wide' : '')}>
                {item.children.map((child) => (
                    <li key={child.url + child.label}>
                        <a href={child.url}>{child.label}</a>
                    </li>
                ))}
            </ul>
        </li>
    );
}

export default function Header({ config }) {
    const [panel, setPanel] = useState(null); // 'search' | 'cart' | 'menu' | null
    const [cart, setCart] = useState(null);
    const [submenu, setSubmenu] = useState(null); // index of the open dropdown
    const root = useRef(null);

    const count = cart ? cart.items_count : 0;

    function loadCart() {
        fetchCart(config.storeApi)
            .then(setCart)
            .catch(() => {
                /* logged-out edge cases / cache layers — leave the badge empty */
            });
    }

    useEffect(() => {
        loadCart();

        const refresh = () => loadCart();
        const openOnAdd = () => {
            loadCart();
            setPanel('cart');
        };

        // Woo Blocks dispatches real DOM CustomEvents…
        document.body.addEventListener('wc-blocks_added_to_cart', openOnAdd);
        document.body.addEventListener('wc-blocks_removed_from_cart', refresh);
        window.addEventListener('pageshow', refresh);

        // …while classic AJAX add-to-cart only fires inside jQuery's own event
        // system, which native listeners never see.
        const jq = window.jQuery;
        if (jq) {
            jq(document.body).on('added_to_cart', openOnAdd);
            jq(document.body).on('removed_from_cart wc_fragments_refreshed wc_fragments_loaded', refresh);
        }

        return () => {
            document.body.removeEventListener('wc-blocks_added_to_cart', openOnAdd);
            document.body.removeEventListener('wc-blocks_removed_from_cart', refresh);
            window.removeEventListener('pageshow', refresh);
            if (jq) {
                jq(document.body).off('added_to_cart', openOnAdd);
                jq(document.body).off('removed_from_cart wc_fragments_refreshed wc_fragments_loaded', refresh);
            }
        };
    }, []);

    /* Lock body scroll while a drawer is open, and close everything on Escape. */
    useEffect(() => {
        if (panel) {
            setSubmenu(null);
        }

        document.body.classList.toggle('tsa-chrome-locked', panel === 'cart' || panel === 'menu');
        document.body.classList.toggle('tsa-search-open', panel === 'search');

        const onKey = (event) => {
            if (event.key === 'Escape') {
                setPanel(null);
            }
        };

        // No dimming overlay for search: the header sits inside a stacking
        // context of its own, so a full-page scrim painted over the dropdown
        // instead of under it. An outside click closes the panel instead.
        const onPointerDown = (event) => {
            if (panel === 'search' && root.current && !root.current.contains(event.target)) {
                setPanel(null);
            }
        };

        document.addEventListener('keydown', onKey);
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('touchstart', onPointerDown, { passive: true });

        return () => {
            document.removeEventListener('keydown', onKey);
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('touchstart', onPointerDown);
        };
    }, [panel]);

    return (
        <div className="tsa-hdr" data-panel={panel || ''} ref={root}>
            <Announcement announce={config.announce} />

            <div className="tsa-hdr__bar">
                <a className="tsa-hdr__logo" href={config.home} aria-label={config.logo.alt}>
                    <img className="m-logo m-dark-logo" src={config.logo.dark} alt={config.logo.alt} width="180" height="60" />
                    <img className="m-logo m-light-logo" src={config.logo.light} alt="" width="180" height="60" aria-hidden="true" />
                </a>

                <nav className="tsa-hdr__nav" aria-label="Primary">
                    <ul>
                        {config.nav.map((item, position) => (
                            <NavItem
                                key={item.label}
                                item={item}
                                open={submenu === position}
                                onOpen={() => setSubmenu(position)}
                                onClose={() => setSubmenu((current) => (current === position ? null : current))}
                            />
                        ))}
                    </ul>
                </nav>

                <div className="tsa-hdr__actions">
                    <button
                        type="button"
                        className="tsa-hdr__icon"
                        aria-label={`Number of items in the cart: ${count}`}
                        aria-expanded={panel === 'cart'}
                        onClick={() => setPanel(panel === 'cart' ? null : 'cart')}
                    >
                        <BagIcon />
                        {count > 0 && <span className="tsa-hdr__badge">{count}</span>}
                    </button>

                    <button
                        type="button"
                        className="tsa-hdr__icon"
                        aria-label="Search"
                        aria-expanded={panel === 'search'}
                        onClick={() => setPanel(panel === 'search' ? null : 'search')}
                    >
                        <SearchIcon />
                    </button>

                    <a className="tsa-hdr__cta" href={config.cta.url}>
                        {config.cta.label}
                    </a>

                    <button
                        type="button"
                        className="tsa-hdr__icon tsa-hdr__burger"
                        aria-label="Menu"
                        aria-expanded={panel === 'menu'}
                        onClick={() => setPanel(panel === 'menu' ? null : 'menu')}
                    >
                        <MenuIcon />
                    </button>
                </div>
            </div>

            <SearchPanel
                open={panel === 'search'}
                onClose={() => setPanel(null)}
                config={config.search}
                calendar={config.calendar}
            />

            {/* Drawers live on <body>: the sticky header's transform would
                otherwise clip their fixed positioning to the header itself. */}
            <Portal>
                <CartDrawer
                    open={panel === 'cart'}
                    onClose={() => setPanel(null)}
                    cart={cart}
                    onChange={setCart}
                    config={config}
                />

                <MobileNav
                    open={panel === 'menu'}
                    onClose={() => setPanel(null)}
                    items={config.nav}
                    config={config}
                    onSearch={() => setPanel('search')}
                />

            </Portal>
        </div>
    );
}
